import { Component, OnInit, ViewChild, ElementRef, forwardRef, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NG_VALIDATORS, AbstractControl, ValidationErrors, Validator, Validators } from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/js/utils';

@Component({
  selector: 'app-phone-country-input',
  templateUrl: './phone-country-input.component.html',
  styleUrls: ['./phone-country-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneCountryInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneCountryInputComponent),
      multi: true
    }
  ]
})
export class PhoneCountryInputComponent implements OnInit, ControlValueAccessor, Validator {
  @ViewChild('phoneInput', { static: true }) phoneInputRef!: ElementRef;
  @Output() blur: EventEmitter<any> = new EventEmitter();

  iti: any;
  value: { countryCode: string; phone: string } = { countryCode: '', phone: '' };
  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit() {
    const inputElement = this.phoneInputRef.nativeElement;
    this.iti = intlTelInput(inputElement, {
      initialCountry: 'IN',
      separateDialCode: true,
      autoPlaceholder: 'off'
    });

    inputElement.addEventListener('input', this.onInputChange.bind(this));
    inputElement.addEventListener('blur', this.onBlur.bind(this));
    inputElement.addEventListener('countrychange', this.onCountryChange.bind(this));
  }

  writeValue(value: any): void {
    if (value && value.countryCode && typeof value.countryCode === 'string') {
      this.value = value;

      // Safely strip '+' if present
      const dialCode = value.countryCode.startsWith('+')
        ? value.countryCode.slice(1)
        : value.countryCode;

      const countryCode = this.getCountryCodeByDialCode(dialCode);
      if (countryCode) {
        this.iti.setCountry(countryCode);
      }

      this.phoneInputRef.nativeElement.value = value.phone ?? '';
    } else {
      this.phoneInputRef.nativeElement.value = '';
      this.iti.setCountry('IN'); // default country
    }
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.phoneInputRef.nativeElement.disabled = isDisabled;
  }

  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');

    const countryData = this.iti.getSelectedCountryData();
    this.value = {
      countryCode: `+${countryData.dialCode}`,
      phone: input.value
    };
    this.onChange(this.value);
  }

  onCountryChange() {
    const input = this.phoneInputRef.nativeElement;

    const countryData = this.iti.getSelectedCountryData();

    this.value = {
      countryCode: `+${countryData.dialCode}`,
      phone: input.value || ''
    };

    // ✅ IMPORTANT: notify Angular
    this.onChange(this.value);
  }

  onBlur(event: FocusEvent) {
    this.onTouched();
    this.blur.emit(this.value); // Emit the blur event to the parent component
  }

  private getCountryCodeByDialCode(dialCode: string): string | null {
    const countryData = intlTelInput.getCountryData();
    const country = countryData.find((c: any) => c.dialCode === dialCode);
    return country ? country.iso2 : null;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    // Check if the field is required
    const isRequired = control.hasValidator(Validators.required);

    // If required, check for non-empty and length between 7 and 15
    if (isRequired) {
      if (!value || !value.phone) {
        return { required: true };
      }
      if (value.phone.length < 7 || value.phone.length > 15) {
        return {
          lengthError: {
            minLength: 7,
            maxLength: 15,
            actualLength: value.phone.length
          }
        };
      }
    } else {
      // If not required, only validate if user has entered a value
      if (
        value &&
        value.phone &&
        value.phone.length > 0 &&
        (value.phone.length < 7 || value.phone.length > 15)
      ) {
        return {
          lengthError: {
            minLength: 7,
            maxLength: 15,
            actualLength: value.phone.length
          }
        };
      }
    }

    return null;
  }


}
